/*
* Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
*/
import React, {ChangeEvent, FocusEvent} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {AutocompleteInputChangeReason} from '@material-ui/lab/Autocomplete';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {TopNavFilterIcon} from '@ion/dls/src/icons/TopNavFilterIcon';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useMedia} from '../../hooks/useMedia';
import AutocompleteSearch, {Option} from '../search/AutocompleteSearch';

export interface SearchPanelHeaderProps {
  onFilter?: Function;
  options: Option[];
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onResultSelect?: (
    event: ChangeEvent<{}>,
    value: unknown | unknown[],
    reason: string
  ) => void;
  onChange?: (
    event: ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  fullWidthOnOpen?: boolean;
  width?: string | number;
  portalClassOverride?: string;
  disablePopperPortal?: boolean;
  loading?: boolean;
  loadingText?: string;
  noResultsText?: string;
  recentSearches: Option[];
  trackingSearchOpen?: boolean;
  isPhoneTrackingSearch?: boolean;
  isClearSearch?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    fontFamily,
    fontStyle,
    fontWeight,
    fontSize,
    lineHeight
  } = dlsAlt('typography', 'searchInput');
  const {
    dls: {
      motion: {
        element: {
          smallExpansion: {
            open: expansionOpen
          }
        }
      }
    }
  } = theme;
  return {
    root : {
      width  : '100%',
      height : (props: {
        isPhoneTrackingSearch: boolean;
      }) => props.isPhoneTrackingSearch ? 'auto' : '64px',
      display         : 'flex',
      backgroundColor : dlsColor('white'),
      flexDirection   : 'row',
      justifyContent  : 'center',
      alignItems      : 'center'
    },
    search : {
      flexGrow   : 1,
      alignItems : 'center',
      display    : 'inline-flex',
      height     : (props: {
        isPhoneTrackingSearch: boolean;
      }) => props.isPhoneTrackingSearch ? 'auto' : '66px'
    },
    input : {
      color : dlsColor('grey3'),
      width : '100%',
      fontFamily,
      fontStyle,
      fontWeight,
      fontSize,
      lineHeight
    },
    filterIcon : {
      marginRight : '20px',
      width       : '48px',
      transition  : `width ${expansionOpen}`
    },
    filterIconHidden : {
      width      : '0px',
      transition : `width ${expansionOpen}`
    }
  };
});

/**
 * @SearchPanelHeader
 *
 * Defines Search Panel Header component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 * <SearchPanelHeader translate={translate} onSearch={onSearch} onFilter={onFilter} />
 *
 * ```
 */
export const SearchPanelHeader = ({
  onFilter,
  options,
  onChange,
  onFocus,
  onBlur,
  onResultSelect,
  loading,
  portalClassOverride,
  recentSearches,
  trackingSearchOpen = false,
  isPhoneTrackingSearch = false,
  isClearSearch = false
}: SearchPanelHeaderProps) => {
  const classes   = useStyles({isPhoneTrackingSearch});
  const {isPhone} = useMedia();

  return (
    <div className={classes.root}>
      <div className={classes.search}>
        <AutocompleteSearch fullWidthOnOpen
          disablePopperPortal
          options={options}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onResultSelect={onResultSelect}
          loading={loading}
          portalClassOverride={portalClassOverride}
          recentSearches={recentSearches}
          shortPlaceholder={!trackingSearchOpen}
          isClearSearch={isClearSearch} />
      </div>
      {
        isPhone && (
          <div className={trackingSearchOpen ? classes.filterIconHidden : classes.filterIcon}>
            <IconButton onClick={onFilter}
              icon={<TopNavFilterIcon />} />
          </div>
        )
      }
    </div>
  );
};

export default SearchPanelHeader;
